import { ReactNode, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { useMediaQuery } from '@givebutter/react-util';
import { maskDecimal } from '@givebutter/util';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    BidStatusFilter,
    chargeBidder as chargeBidderApi,
    fetchAuctionBids,
    invoiceBidder as invoiceBidderApi,
} from 'api/campaigns';
import { generateExports } from 'api/contacts/exports';
import * as DropdownMenu from 'components/dropdown-menu';
import {
    BidActionPill,
    BidStatusContainer,
    BidStatusTooltip,
} from 'components/pages/campaign-settings/campaign-auction/BidStatus';
import { CopyContactInfo } from 'components/pages/campaign-settings/campaign-auction/CopyContactInfo';
import { ItemPaymentDetails } from 'components/pages/campaign-settings/campaign-auction/ItemPaymentDetails';
import { MarkUncollectableModal } from 'components/pages/campaign-settings/campaign-auction/sold-items/MarkUncollectable';
import { TableConstants, sortQuery, useReactTableState } from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { TruncatedText } from 'components/truncated-text';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { AuctionItem, Bid } from 'types/domain';

const WINNING_BID_STATUS: BidStatusFilter = 'all_winners';

const columnHelper = createColumnHelper<Bid>();

const ColumnIds = {
    ExpandAction: TableConstants.Expand,
    ItemName: 'item_name',
    Winner: 'bidder_name',
    ItemId: 'item_id',
    SoldPrice: 'amount',
    Status: 'status',
    ExtraActions: TableConstants.Actions,
};

const useSoldItems = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const currencySymbol = useCurrencySymbol();
    const queryClient = useQueryClient();
    const isMobile = useMediaQuery(`(max-width: 640px)`);
    const {
        expanded,
        pagination,
        setPagination,
        sorting,
        setSorting,
        setExpanded,
        columnVisibility,
        setColumnVisibility,
    } = useReactTableState();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = Number(campaign.id);
    const auctionId = Number(campaign.auction_id);
    const [search, setSearch] = usePersistentSearch();
    const searchTerm = useDebounce(search);

    useEffect(() => {
        if (isMobile) {
            setColumnVisibility({
                [ColumnIds.Winner]: false,
                [ColumnIds.ItemId]: false,
            });
        } else {
            setColumnVisibility({});
        }
    }, [isMobile, setColumnVisibility]);

    const [isPending, params] = useIsPending(
        {
            status: WINNING_BID_STATUS,
            page: pagination.pageIndex + 1,
            query: searchTerm ?? '',
            ...sortQuery(sorting),
        },
        500,
    );

    const { data } = useSuspenseQuery({
        queryKey: campaignQueryKeys.auctionBids(campaignId, auctionId, params),
        queryFn: () =>
            fetchAuctionBids({
                campaignId,
                auctionId,
                ...params,
            }),
    });

    const invalidate = () =>
        queryClient.invalidateQueries({
            queryKey: campaignQueryKeys.auction(campaignId, auctionId),
        });

    const { mutate: chargeBidder } = useMutation({
        mutationFn: (bidId: number) => chargeBidderApi(campaignId, auctionId, bidId),
        onSuccess: () => {
            toast.success('Successfully charged bidder');
            invalidate();
        },
        onError: () => {
            toast.error('Unable to charge bidder');
        },
    });

    const { mutate: invoiceBidder } = useMutation({
        mutationFn: (bidderId: number) => invoiceBidderApi(campaignId, auctionId, bidderId),
        onSuccess: () => {
            toast.success('Successfully resent invoice to bidder');
            invalidate();
        },
        onError: () => {
            toast.error('Unable to resend invoice to bidder');
        },
    });

    const { isPending: isGeneratingReport, mutateAsync: onSubmitExportReport } = useMutation({
        mutationFn: () =>
            generateExports(accountId, {
                scope: 'all',
                object: 'auction',
                report_type: 'csv',
                resource: 'winning_bids',
                object_id: auctionId,
            }),
        onSuccess: () =>
            toast.success(
                <p>
                    <span className="font-medium">Success!</span> We'll send you an email when your
                    export is ready.
                </p>,
            ),
        onError: () =>
            toast.error(
                <p>
                    <span className="font-medium">Whoops.</span> Something went wrong. Please try
                    again.
                </p>,
            ),
    });

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: ColumnIds.ExpandAction,
                cell: info => {
                    const isExpanded = info.row.getIsExpanded();

                    return (
                        <Button
                            variant="none"
                            color="neutral"
                            aria-expanded={isExpanded}
                            aria-label="Toggle Auction Item Info"
                            onClick={() => {
                                info.row.toggleExpanded(!isExpanded);
                            }}
                        >
                            <FontAwesomeIcon
                                className={`${isExpanded ? 'rotate-90' : ''}`}
                                icon={faChevronRight}
                            />
                        </Button>
                    );
                },
                meta: {
                    className: 'pr-0 w-8',
                },
            }),
            columnHelper.accessor('item_name', {
                id: ColumnIds.ItemName,
                header: 'Item',
                cell: info => {
                    return (
                        <div className="flex max-w-[12rem] font-medium">
                            <TruncatedText>{info.getValue()}</TruncatedText>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('bidder', {
                id: ColumnIds.Winner,
                header: 'Winner',
                cell: info => {
                    const { first_name: firstName, last_name: lastName } = info.getValue();
                    return <div className={'font-medium'}>{`${firstName} ${lastName}`}</div>;
                },
            }),
            columnHelper.accessor('item_id', {
                id: ColumnIds.ItemId,
                header: 'ID #',
                cell: info => {
                    return <div>{info.getValue()}</div>;
                },
            }),
            columnHelper.accessor('amount', {
                id: ColumnIds.SoldPrice,
                header: 'Sold price',
                cell: info => {
                    return (
                        <div className={'flex items-center gap-x-2'}>
                            <p className={'font-medium'}>
                                {currencySymbol}
                                {maskDecimal(info.getValue())}
                            </p>
                            <BidStatusTooltip status={info.row.original.status} />
                        </div>
                    );
                },
            }),
            columnHelper.accessor('dashboard_status', {
                id: ColumnIds.Status,
                enableSorting: false,
                header: 'Status',
                cell: info => {
                    return (
                        <BidActionPill
                            className={
                                'w-fit whitespace-nowrap rounded-full px-2 py-1 text-center text-sm'
                            }
                            status={info.getValue()}
                        />
                    );
                },
                meta: {
                    className: 'p-0',
                },
            }),
            columnHelper.display({
                id: ColumnIds.ExtraActions,
                cell: info => {
                    const bid = info.row.original;
                    const dropdownDisabledClass =
                        '!cursor-not-allowed hover:!bg-transparent opacity-60';
                    const dropdownButtonDisabledClass = 'pointer-events-none';
                    const isChargedDisabled = !bid.can_charge;
                    const chargeDropdownDisabledClass = isChargedDisabled
                        ? dropdownDisabledClass
                        : '';
                    const chargeButtonDisabledClass = isChargedDisabled
                        ? dropdownButtonDisabledClass
                        : '';
                    const isUncollectableDisabled = !bid.can_disqualify;
                    const uncollectableDropdownDisabledClass = isUncollectableDisabled
                        ? dropdownDisabledClass
                        : '';
                    const uncollectableButtonDisabledClass = isUncollectableDisabled
                        ? dropdownButtonDisabledClass
                        : '';
                    const isInvoiceableDisabled = !bid.bidder.can_invoice;
                    const invoiceDropdownDisabledClass = isInvoiceableDisabled
                        ? dropdownDisabledClass
                        : '';
                    const invoiceButtonDisabledClass = isInvoiceableDisabled
                        ? dropdownButtonDisabledClass
                        : '';

                    return (
                        <DropdownMenu.Root>
                            <DropdownMenu.EllipsisTrigger className="float-right px-2 py-1" />
                            <DropdownMenu.Content align={'end'}>
                                <DropdownMenu.Item
                                    className={`!p-0 ${invoiceDropdownDisabledClass}`}
                                    onClick={e => e.preventDefault()}
                                >
                                    <AlertDialog.Root type="warning">
                                        <AlertDialog.Trigger asChild={true}>
                                            <p
                                                className={`px-1 py-2 text-sm ${invoiceButtonDisabledClass}`}
                                            >
                                                Resend invoice
                                            </p>
                                        </AlertDialog.Trigger>
                                        <AlertDialog.Portal>
                                            <AlertDialog.Overlay />
                                            <AlertDialog.Content>
                                                <AlertDialog.Header>
                                                    <AlertDialog.Icon />
                                                    <AlertDialog.Close />
                                                </AlertDialog.Header>
                                                <AlertDialog.Body>
                                                    <AlertDialog.Title>
                                                        Resend invoice?
                                                    </AlertDialog.Title>
                                                    <AlertDialog.Description>
                                                        Resending an invoice will send the winning
                                                        bidder a payment request so that they can
                                                        complete the purchase of their won items.
                                                    </AlertDialog.Description>
                                                </AlertDialog.Body>
                                                <AlertDialog.Footer>
                                                    <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                                    <AlertDialog.Action
                                                        onClick={() => invoiceBidder(bid.bidder.id)}
                                                    >
                                                        Resend invoice
                                                    </AlertDialog.Action>
                                                </AlertDialog.Footer>
                                            </AlertDialog.Content>
                                        </AlertDialog.Portal>
                                    </AlertDialog.Root>
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                    className={`!p-0 ${uncollectableDropdownDisabledClass}`}
                                    onClick={e => e.preventDefault()}
                                >
                                    <MarkUncollectableModal
                                        bid={bid}
                                        className={uncollectableDropdownDisabledClass}
                                    >
                                        <AlertDialog.Trigger asChild={true}>
                                            <p
                                                className={`px-1 py-2 text-sm ${uncollectableButtonDisabledClass}`}
                                            >
                                                Mark uncollectable
                                            </p>
                                        </AlertDialog.Trigger>
                                    </MarkUncollectableModal>
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                    className={`!p-0 ${chargeDropdownDisabledClass}`}
                                    onClick={e => e.preventDefault()}
                                >
                                    <AlertDialog.Root type="warning">
                                        <AlertDialog.Trigger asChild={true}>
                                            <p
                                                className={`px-1 py-2 text-sm ${chargeButtonDisabledClass}`}
                                            >
                                                Charge bidder
                                            </p>
                                        </AlertDialog.Trigger>
                                        <AlertDialog.Portal>
                                            <AlertDialog.Overlay />
                                            <AlertDialog.Content>
                                                <AlertDialog.Header>
                                                    <AlertDialog.Icon />
                                                    <AlertDialog.Close />
                                                </AlertDialog.Header>
                                                <AlertDialog.Body>
                                                    <AlertDialog.Title>
                                                        Charge bidder?
                                                    </AlertDialog.Title>
                                                    <AlertDialog.Description>
                                                        Successfully collected payments will incur a
                                                        5% platform fee in addition to standard
                                                        processing fees.
                                                    </AlertDialog.Description>
                                                </AlertDialog.Body>
                                                <AlertDialog.Footer>
                                                    <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                                    <AlertDialog.Action
                                                        onClick={() => chargeBidder(bid.id)}
                                                    >
                                                        Charge bidder
                                                    </AlertDialog.Action>
                                                </AlertDialog.Footer>
                                            </AlertDialog.Content>
                                        </AlertDialog.Portal>
                                    </AlertDialog.Root>
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [chargeBidder, currencySymbol, invoiceBidder],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            expanded,
            columnVisibility,
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onExpandedChange: setExpanded,
        manualPagination: true,
        enableSorting: true,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            renderExpandedRow: row => {
                const getCellById = (id: string) =>
                    row.getAllCells().find(cell => cell.column.id === id);
                const [winnerCell, bidIdCell] = [ColumnIds.Winner, ColumnIds.ItemId].map(
                    getCellById,
                );

                return (
                    <tr>
                        <td
                            className={'w-min bg-white px-4 pb-4'}
                            colSpan={row.getVisibleCells().length}
                        >
                            <div className={'grid grid-cols-2 gap-4 md:gap-8'}>
                                <div
                                    className={'col-span-full flex flex-col gap-y-4 md:col-span-1'}
                                >
                                    <ItemPaymentDetails
                                        item={
                                            {
                                                id: row.original.item_id,
                                                name: row.original.item_name,
                                                price: row.original.amount,
                                                pictures: [
                                                    {
                                                        url: row.original.item_image,
                                                    },
                                                ],
                                            } as AuctionItem
                                        }
                                    />
                                    {(row.original.status === 'winner_paid' ||
                                        row.original.status === 'buy_now') &&
                                        !!row.original.transaction && (
                                            <div className={'flex justify-end'}>
                                                <Link
                                                    to={`/accounts/${accountId}/transactions/${row.original.transaction?.id}`}
                                                    className={'text-sm text-blue-500'}
                                                >
                                                    View Transaction
                                                </Link>
                                            </div>
                                        )}
                                    <BidStatusContainer
                                        className={
                                            'w-full px-2 py-1 text-center text-base font-medium'
                                        }
                                        status={row.original.status}
                                    />
                                </div>
                                <div className={'col-span-full flex gap-x-4 sm:hidden'}>
                                    {winnerCell && (
                                        <div className={'flex flex-col gap-y-2'}>
                                            <p className={'text-xs text-gray-500'}>
                                                {winnerCell.column.columnDef.header as ReactNode}
                                            </p>
                                            <p className={'text-sm font-medium'}>
                                                {flexRender(
                                                    winnerCell.column.columnDef.cell,
                                                    winnerCell.getContext(),
                                                )}
                                            </p>
                                        </div>
                                    )}
                                    {bidIdCell && (
                                        <div className={'flex flex-col gap-y-2'}>
                                            <p className={'text-xs text-gray-500'}>
                                                {bidIdCell.column.columnDef.header as ReactNode}
                                            </p>
                                            <p className={'text-sm font-medium'}>
                                                {flexRender(
                                                    bidIdCell.column.columnDef.cell,
                                                    bidIdCell.getContext(),
                                                )}
                                            </p>
                                        </div>
                                    )}
                                    <div />
                                </div>
                                <CopyContactInfo
                                    email={row.original.bidder.email}
                                    phone={row.original.bidder.phone}
                                    address={row.original.bidder.address}
                                    className={'col-span-full flex flex-col gap-y-4 md:col-span-1'}
                                />
                            </div>
                        </td>
                    </tr>
                );
            },
        },
    });

    useSyncPaginatedData(table, data);

    return {
        accountId,
        table,
        onSubmitExportReport,
        isGeneratingReport,
        search,
        setSearch,
        searchTerm,
        isPending,
    };
};

export { useSoldItems, ColumnIds };
